/**
 * Film hero: a clipped, sticky frame opens to the viewport over its scroll
 * runway. The media scales only 4.5%; typography stays at a readable size.
 * The bundled MP4 plays forward with a baked-in crossfade at the loop seam;
 * native looping handles the restart without timers or per-frame seeking.
 */
(() => {
if (customElements.get('sam-film-hero')) return;

class SamFilmHero extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.video = this.querySelector('[data-hero-video]');
    this.viewport = this.querySelector('.sam-film-hero__viewport');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isVisible = false;
    this.frameRequest = 0;

    this.classList.add('is-enhanced');
    this.onScroll = () => {
      if (this.frameRequest) return;
      this.frameRequest = requestAnimationFrame(() => {
        this.frameRequest = 0;
        this.updateFrame();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true, signal });
    window.addEventListener('resize', this.onScroll, { passive: true, signal });
    this.motionQuery.addEventListener('change', () => {
      if (this.motionQuery.matches) this.classList.remove('has-played');
      this.updatePlayback();
      this.onScroll();
    }, { signal });
    document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });

    if (this.video) {
      this.video.muted = true;
      this.video.addEventListener('playing', () => {
        this.classList.add('has-played');
      }, { signal });
      this.video.addEventListener('error', () => {
        this.classList.remove('has-played');
      }, { signal });
    }

    this.observer = new IntersectionObserver(([entry]) => {
      this.isVisible = entry.isIntersecting;
      this.updatePlayback();
    }, { threshold: 0 });
    this.observer.observe(this.viewport);
    this.updateFrame();
  }

  updateFrame() {
    if (this.dataset.scrollExpand !== 'true' || this.motionQuery.matches) {
      this.style.setProperty('--hero-progress', '0');
      return;
    }
    const bounds = this.getBoundingClientRect();
    const distance = Math.max(1, this.offsetHeight - this.viewport.offsetHeight);
    const progress = Math.max(0, Math.min(1, -bounds.top / distance));
    const eased = progress * progress * (3 - 2 * progress);
    this.style.setProperty('--hero-progress', eased.toFixed(4));
  }

  updatePlayback() {
    if (!this.video) return;
    const mayAutoplay = this.dataset.autoplay === 'true'
      && !this.motionQuery.matches
      && !navigator.connection?.saveData;
    if (this.isVisible && !document.hidden && mayAutoplay) {
      this.playVideo();
    } else {
      this.video.pause();
    }
  }

  playVideo() {
    if (!this.video || this.video.error) return;
    if (!this.video.dataset.loaded) {
      this.video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
      });
      this.video.dataset.loaded = 'true';
      this.video.load();
    }
    this.video.play().catch(() => this.classList.remove('has-played'));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frameRequest);
    this.video?.pause();
  }
}

customElements.define('sam-film-hero', SamFilmHero);
})();
